import { addDays, addMonths, differenceInMonths, format, isBefore, parseISO } from 'date-fns';
import { supabase } from './supabase';
import { BRANDING, isForfettario } from './branding';
import { oggiRoma } from './dataItalia';
import {
    calculateInvoiceTotals,
    createInvoice,
    issueDraftOnFattureInCloud,
    type CalculatedInvoiceTotals,
    type InvoiceItemInput,
    type InvoicePayload,
} from './invoiceUtils';
import { DEFAULT_NO_VAT_FORFETTARIO_CLAUSE } from './noVatClause';
import type { Database } from '../types/database.types';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];

/** Range di mesi fatturabili in una sola fattura di abbonamento. */
export const MIN_SUBSCRIPTION_INVOICE_MONTHS = 1;
export const MAX_SUBSCRIPTION_INVOICE_MONTHS = 12;
export const SUBSCRIPTION_MONTH_OPTIONS = Array.from(
    { length: MAX_SUBSCRIPTION_INVOICE_MONTHS - MIN_SUBSCRIPTION_INVOICE_MONTHS + 1 },
    (_, i) => i + MIN_SUBSCRIPTION_INVOICE_MONTHS
);

export const SUBSCRIPTION_INVOICE_UNIT = 'mese';

/** Giorni di anticipo con cui il giro notturno prepara la bozza di un periodo.
 *  Stesso valore del job in supabase/migrations/00000000000022_bozze_abbonamenti_notturne.sql. */
export const AUTO_INVOICE_LEAD_DAYS = 7;

/** Cadenze di fatturazione proposte nel form del contratto. */
export const BILLING_PERIOD_OPTIONS: { months: number; label: string }[] = [
    { months: 1, label: 'Mensile' },
    { months: 3, label: 'Trimestrale' },
    { months: 6, label: 'Semestrale' },
    { months: 12, label: 'Annuale' },
];

export const getBillingPeriodLabel = (months: number | null | undefined): string =>
    BILLING_PERIOD_OPTIONS.find((o) => o.months === months)?.label ?? `Ogni ${months ?? 1} mesi`;

export interface SubscriptionInvoicePeriod {
    /** Primo giorno del periodo fatturato (ISO yyyy-MM-dd). */
    start: string;
    /** Primo giorno NON coperto dalla fattura = nuova "prossima fattura" (ISO yyyy-MM-dd). */
    end: string;
}

/** Subset di customer_subscriptions necessario per fatturare. */
export interface InvoiceableSubscription {
    id: string;
    customer_id: string;
    monthly_amount: number;
    next_billing_date: string;
    billing_anchor_date?: string | null;
    billing_period_months?: number | null;
}

const clampMonths = (months: number): number =>
    Math.min(MAX_SUBSCRIPTION_INVOICE_MONTHS, Math.max(MIN_SUBSCRIPTION_INVOICE_MONTHS, Math.round(months)));

const toISO = (date: Date): string => format(date, 'yyyy-MM-dd');

const formatItalianDate = (isoDate: string): string => format(parseISO(isoDate), 'dd/MM/yyyy');

/** Mesi di calendario fra due date, ignorando il giorno. */
const calendarMonthsBetween = (from: Date, to: Date): number =>
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());

/**
 * Periodo coperto da una fattura di N mesi a partire da `periodStart`.
 * `end` è esclusivo: coincide con la nuova data della prossima fattura.
 *
 * Si conta dal giorno di riferimento (`anchor`) e non dall'inizio del periodo:
 * sommare un mese alla volta fa 31/01 -> 28/02 -> 28/03 e da lì si resta sul
 * 28. Contando dal riferimento: 31/01 -> 28/02 -> 31/03. Se l'inizio del
 * periodo non cade sulla griglia del riferimento (data spostata a mano), si
 * riparte dall'inizio del periodo.
 *
 * Stessa regola della funzione SQL `subscription_period_end`
 * (supabase/migrations/00000000000021_fatturazione_abbonamenti.sql): se cambi
 * l'una, cambia l'altra.
 */
export const getSubscriptionInvoicePeriod = (
    periodStart: string,
    months: number,
    anchor?: string | null
): SubscriptionInvoicePeriod => {
    const start = parseISO(periodStart);
    const mesi = clampMonths(months);
    const fallback = { start: periodStart, end: toISO(addMonths(start, mesi)) };

    if (!anchor || anchor > periodStart) return fallback;

    const base = parseISO(anchor);
    const trascorsi = calendarMonthsBetween(base, start);
    if (toISO(addMonths(base, trascorsi)) !== periodStart) return fallback;

    return { start: periodStart, end: toISO(addMonths(base, trascorsi + mesi)) };
};

/** "dal 01/10/2026 al 31/10/2026": sul documento il periodo si chiude il giorno prima di `end`. */
export const formatSubscriptionPeriod = (period: SubscriptionInvoicePeriod): string =>
    `dal ${formatItalianDate(period.start)} al ${formatItalianDate(toISO(addDays(parseISO(period.end), -1)))}`;

/**
 * Mesi suggeriti per un periodo già iniziato: quanti ne servono per coprire
 * il periodo scoperto fino a oggi (min 1, max 12).
 */
export const getSuggestedInvoiceMonths = (
    nextBillingDate: string,
    today: Date = new Date()
): number => {
    const renewal = parseISO(nextBillingDate);
    const fullMonths = differenceInMonths(today, renewal);
    if (fullMonths < 0) return MIN_SUBSCRIPTION_INVOICE_MONTHS;
    const covered = addMonths(renewal, fullMonths);
    return clampMonths(isBefore(covered, today) ? fullMonths + 1 : fullMonths);
};

/**
 * Quante bozze preparerebbe il giro notturno per un contratto con questa
 * "prossima fattura": una per ogni periodo che inizia entro l'anticipo, al
 * massimo 12. Serve all'avviso "stanotte verranno preparate N bozze".
 */
export const countUpcomingDrafts = (
    nextBillingDate: string,
    months: number,
    anchor: string | null,
    today: string = oggiRoma(),
    leadDays: number = AUTO_INVOICE_LEAD_DAYS
): number => {
    const limite = toISO(addDays(parseISO(today), leadDays));
    let inizio = nextBillingDate;
    let bozze = 0;
    while (inizio <= limite && bozze < MAX_SUBSCRIPTION_INVOICE_MONTHS) {
        bozze += 1;
        inizio = getSubscriptionInvoicePeriod(inizio, months, anchor).end;
    }
    return bozze;
};

/** Riga fattura per N mesi di abbonamento. */
export const buildSubscriptionInvoiceItems = (
    subscription: InvoiceableSubscription,
    months: number,
    period: SubscriptionInvoicePeriod,
    vatRate: number = BRANDING.aliquotaIvaDefault
): InvoiceItemInput[] => [
    {
        product_id: null,
        description: `Abbonamento servizi ${months} ${months === 1 ? 'mese' : 'mesi'} - periodo ${formatSubscriptionPeriod(period)}`,
        quantity: months,
        unit: SUBSCRIPTION_INVOICE_UNIT,
        unit_price: subscription.monthly_amount,
        discount_percentage: 0,
        vat_rate: vatRate,
    },
];

/** Anteprima totali (imponibile, IVA, marca da bollo) senza scrivere su DB. */
export const previewSubscriptionInvoiceTotals = (
    subscription: InvoiceableSubscription,
    months: number,
    period: SubscriptionInvoicePeriod,
    noVatForfettario: boolean
): CalculatedInvoiceTotals =>
    calculateInvoiceTotals(
        buildSubscriptionInvoiceItems(subscription, months, period),
        noVatForfettario
    );

export interface CreateSubscriptionInvoiceParams {
    subscription: InvoiceableSubscription;
    months: number;
    /** true = dopo la bozza, emette subito su Fatture in Cloud. */
    issueNow: boolean;
    /** Default: il regime dell'azienda. */
    noVatForfettario?: boolean;
    noVatClauseText?: string | null;
    iban?: string | null;
}

export interface CreateSubscriptionInvoiceResult {
    /** La fattura emessa, oppure la bozza se non è stata emessa. */
    invoice: InvoiceRow;
    period: SubscriptionInvoicePeriod;
    /** Valorizzato se la bozza c'è ma "prossima fattura" non è stata spostata. */
    billingError?: string;
    /** Valorizzato se la bozza c'è ma l'emissione su Fatture in Cloud è fallita. */
    issueError?: string;
}

/**
 * Fattura a mano il prossimo periodo di un abbonamento.
 *
 * Nasce sempre prima una bozza legata al periodo, anche quando si vuole
 * emettere subito: il database rifiuta un secondo documento sullo stesso
 * periodo, quindi è la bozza a fare da lucchetto. Emettere direttamente su
 * Fatture in Cloud e controllare dopo creerebbe il documento numerato anche
 * quando il periodo era già fatturato.
 */
export const createSubscriptionInvoice = async (
    params: CreateSubscriptionInvoiceParams
): Promise<CreateSubscriptionInvoiceResult> => {
    const { subscription, months } = params;

    if (!Number.isInteger(months) || months < MIN_SUBSCRIPTION_INVOICE_MONTHS || months > MAX_SUBSCRIPTION_INVOICE_MONTHS) {
        throw new Error(`I mesi da fatturare devono essere tra ${MIN_SUBSCRIPTION_INVOICE_MONTHS} e ${MAX_SUBSCRIPTION_INVOICE_MONTHS}.`);
    }
    if (!subscription.customer_id) {
        throw new Error('Abbonamento senza cliente associato.');
    }
    if (!(subscription.monthly_amount > 0)) {
        throw new Error('Importo mensile non valido: impossibile fatturare.');
    }

    const period = getSubscriptionInvoicePeriod(
        subscription.next_billing_date,
        months,
        subscription.billing_anchor_date
    );
    const noVatForfettario = params.noVatForfettario ?? isForfettario();

    const payload: InvoicePayload = {
        customer_id: subscription.customer_id,
        invoice_date: oggiRoma(),
        status: 'draft',
        document_type: 'invoice',
        items: buildSubscriptionInvoiceItems(subscription, months, period),
        no_vat_forfettario: noVatForfettario,
        no_vat_clause_text: noVatForfettario
            ? (params.noVatClauseText ?? DEFAULT_NO_VAT_FORFETTARIO_CLAUSE)
            : null,
        iban: params.iban ?? null,
        causale: `Abbonamento ${formatSubscriptionPeriod(period)}`,
        subscription_id: subscription.id,
        subscription_period_start: period.start,
        subscription_period_end: period.end,
    };

    let draft: InvoiceRow;
    try {
        draft = await createInvoice(payload);
    } catch (err) {
        if ((err as { code?: string })?.code === '23505') {
            throw new Error(
                `Il periodo che inizia il ${formatItalianDate(period.start)} è già fatturato per questo abbonamento: la bozza è in Fatture.`
            );
        }
        throw err;
    }

    // "Prossima fattura" avanza solo se nel frattempo non l'ha già spostata il
    // giro notturno: altrimenti si salterebbe un periodo.
    const { error: billingErr } = await supabase
        .from('customer_subscriptions')
        .update({ next_billing_date: period.end })
        .eq('id', subscription.id)
        .eq('next_billing_date', period.start);

    const billingError = billingErr ? (billingErr.message ?? 'Errore aggiornamento prossima fattura') : undefined;

    if (!params.issueNow) {
        return { invoice: draft, period, billingError };
    }

    try {
        const issued = await issueDraftOnFattureInCloud(draft.id);
        return { invoice: issued, period, billingError };
    } catch (err) {
        return {
            invoice: draft,
            period,
            billingError,
            issueError: (err as Error)?.message ?? 'Emissione non riuscita',
        };
    }
};

// ---------------------------------------------------------------------------
// Bozze preparate di notte
// ---------------------------------------------------------------------------

/** Pagina Fatture filtrata sulle bozze: dove portano banner e avvisi. */
export const SUBSCRIPTION_DRAFTS_PATH = '/invoices?status=draft';

/** Segnale che le bozze sono cambiate (emessa, eliminata, creata): pallino e
 *  banner si aggiornano subito invece di aspettare il prossimo controllo. */
export const SUBSCRIPTION_DRAFTS_CHANGED_EVENT = 'pixel:bozze-abbonamenti-cambiate';

export const notifySubscriptionDraftsChanged = (): void => {
    window.dispatchEvent(new Event(SUBSCRIPTION_DRAFTS_CHANGED_EVENT));
};

export interface SubscriptionDraftInvoice {
    id: string;
    invoice_date: string;
    total_amount: number;
    subscription_period_start: string | null;
    subscription_period_end: string | null;
    created_at: string;
    customers: { company_name: string } | null;
}

const DRAFT_SELECT =
    'id, invoice_date, total_amount, subscription_period_start, subscription_period_end, created_at, customers(company_name)';

/** Bozze di abbonamento ancora da emettere, dalla più vecchia. */
export const fetchSubscriptionDraftInvoices = async (): Promise<SubscriptionDraftInvoice[]> => {
    const { data, error } = await supabase
        .from('invoices')
        .select(DRAFT_SELECT)
        .eq('status', 'draft')
        .not('subscription_id', 'is', null)
        .order('subscription_period_start', { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as SubscriptionDraftInvoice[];
};

export interface AutoInvoiceRun {
    quando: string;
    bozze_create: number;
    errori: { contratto: string; cliente: string | null; errore: string }[];
}

/** Esito dell'ultimo giro notturno, scritto dal database in app_settings. */
export const fetchLastAutoInvoiceRun = async (): Promise<AutoInvoiceRun | null> => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'bozze_abbonamenti_ultimo_giro')
        .maybeSingle();
    if (error || !data) return null;
    const valore = (data as { value: unknown }).value as Partial<AutoInvoiceRun> | null;
    if (!valore?.quando) return null;
    return {
        quando: valore.quando,
        bozze_create: Number(valore.bozze_create) || 0,
        errori: Array.isArray(valore.errori) ? valore.errori : [],
    };
};
